import { useState } from "react";
import { api } from "../api";
import { Button } from "./ui/button";
import { ModalShell } from "./ui/modal";
import { useLanguage } from "../hooks/useLanguage";

interface CreateShortLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (link: any) => void;
}

function normalizeUrl(url: string): string {
  const trimmed = url.trim();

  // If already has https://, return as is
  if (trimmed.startsWith('https://')) {
    return trimmed;
  }

  // If has http://, convert to https:// and remove www. if present
  if (trimmed.startsWith('http://')) {
    let domain = trimmed.substring(7); // Remove 'http://'
    if (domain.startsWith('www.')) {
      domain = domain.substring(4); // Remove 'www.'
    }
    return `https://${domain}`;
  }

  // If starts with www., remove www. and add https://
  if (trimmed.startsWith('www.')) {
    return `https://${trimmed.substring(4)}`;
  }

  // If it's just a domain (contains dot and no spaces), add https://
  if (trimmed.includes('.') && !trimmed.includes(' ')) {
    return `https://${trimmed}`;
  }

  // Otherwise, return as is (might be invalid, but let server handle it)
  return trimmed;
}

export function CreateShortLinkModal({ open, onClose, onSuccess }: CreateShortLinkModalProps) {
  const { t } = useLanguage();
  const [targetUrl, setTargetUrl] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetUrl.trim()) return;

    setLoading(true);
    try {
      const normalizedUrl = normalizeUrl(targetUrl.trim());
      const res = await api.createShortLink(normalizedUrl, title.trim() || undefined);
      if (res.ok) {
        onSuccess(res.data);
        handleClose();
      } else {
        alert(t.modals.createShortLink.error + res.error);
      }
    } catch (error) {
      alert(t.modals.createShortLink.error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTargetUrl("");
    setTitle("");
    setLoading(false);
    onClose();
  };

  return (
    <ModalShell open={open} onClose={handleClose}>
      <div className="bg-white rounded-3xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">{t.modals.createShortLink.title}</h2>
          <Button
            variant="close"
            onClick={handleClose}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t.modals.createShortLink.targetUrl}
            </label>
            <input
              type="text"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              placeholder={t.modals.createShortLink.targetUrlPlaceholder}
              className="w-full px-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t.modals.createShortLink.optionalTitle}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.modals.createShortLink.linkName}
              className="w-full px-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#0DAFFD] focus:border-[#0DAFFD] transition-colors"
              disabled={loading}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={!targetUrl.trim() || loading}
              className="flex-1"
            >
              {loading ? t.modals.createShortLink.creating : t.modals.createShortLink.createLink}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={loading}
            >
              {t.modals.createShortLink.cancel}
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
}
